import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table,
  Button,
  Space,
  Form,
  Input,
  Switch,
  Tag,
  message,
  Card,
  Typography,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import { COLOR_PRIMARY } from '../../utils/colors';

const { Title, Text } = Typography;

const OpcDrivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editingKey, setEditingKey] = useState(null);
  const [editForm] = Form.useForm();
  const [addForm] = Form.useForm();
  const firstFieldRef = useRef(null);

  const fetchDrivers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.drivers.getAll();
      setDrivers(Array.isArray(res.data) ? res.data : res.data?.drivers || []);
    } catch {
      message.error('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  const isEditing = (record) => record._id === editingKey;

  const startEdit = (record) => {
    editForm.setFieldsValue({
      name: record.name,
      phoneNumber: record.phoneNumber,
      licenseNumber: record.licenseNumber,
      isExternal: record.isExternal || false,
    });
    setEditingKey(record._id);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    editForm.resetFields();
  };

  const saveEdit = async (record) => {
    try {
      const values = await editForm.validateFields();
      await apiService.drivers.update(record._id, values);
      setEditingKey(null);
      message.success('Saved');
      fetchDrivers();
    } catch (e) {
      if (e?.response?.data?.message) message.error(e.response.data.message);
    }
  };

  const handleAdd = async () => {
    try {
      const values = await addForm.validateFields();
      await apiService.drivers.create(values);
      addForm.resetFields();
      message.success('Driver added');
      fetchDrivers();
      setTimeout(() => firstFieldRef.current?.focus(), 100);
    } catch (e) {
      if (e?.response?.data?.message) message.error(e.response.data.message);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="name" noStyle rules={[{ required: true, message: ' ' }]}>
            <Input size="small" style={{ width: 180 }} placeholder="Name" />
          </Form.Item>
        ) : (
          <Text strong>{v}</Text>
        ),
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="phoneNumber" noStyle>
            <Input size="small" style={{ width: 150 }} placeholder="Phone" />
          </Form.Item>
        ) : (
          v || <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'License No.',
      dataIndex: 'licenseNumber',
      key: 'licenseNumber',
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="licenseNumber" noStyle>
            <Input size="small" style={{ width: 150 }} placeholder="License No." />
          </Form.Item>
        ) : (
          v || <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'External',
      dataIndex: 'isExternal',
      key: 'isExternal',
      width: 90,
      align: 'center',
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="isExternal" noStyle valuePropName="checked">
            <Switch size="small" />
          </Form.Item>
        ) : (
          v ? <Tag color="orange">External</Tag> : <Text type="secondary">—</Text>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 72,
      align: 'right',
      render: (_, record) =>
        isEditing(record) ? (
          <Space size={2}>
            <Button type="text" size="small" icon={<CheckOutlined style={{ color: '#3f8600' }} />} onClick={() => saveEdit(record)} />
            <Button type="text" size="small" icon={<CloseOutlined />} onClick={cancelEdit} />
          </Space>
        ) : (
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEdit(record)} />
        ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>My Drivers</Title>
      </div>

      {/* Add form */}
      <Card
        size="small"
        style={{ marginBottom: 12, borderTop: `2px solid ${COLOR_PRIMARY}`, background: 'rgba(22,119,255,0.06)' }}
        bodyStyle={{ padding: '10px 12px' }}
      >
        <Form form={addForm} onFinish={handleAdd}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div style={labelStyle}>Name *</div>
              <Form.Item name="name" noStyle rules={[{ required: true, message: ' ' }]}>
                <Input ref={firstFieldRef} size="small" style={{ width: 180 }} placeholder="Driver name" />
              </Form.Item>
            </div>
            <div>
              <div style={labelStyle}>Phone</div>
              <Form.Item name="phoneNumber" noStyle>
                <Input size="small" style={{ width: 150 }} placeholder="Phone" />
              </Form.Item>
            </div>
            <div>
              <div style={labelStyle}>License No.</div>
              <Form.Item name="licenseNumber" noStyle>
                <Input size="small" style={{ width: 150 }} placeholder="License No." onPressEnter={handleAdd} />
              </Form.Item>
            </div>
            <div style={{ alignSelf: 'flex-end', display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 2 }}>
              <Form.Item name="isExternal" noStyle valuePropName="checked" initialValue={false}>
                <Switch size="small" />
              </Form.Item>
              <span style={{ fontSize: 12, color: '#888' }}>External</span>
            </div>
            <div style={{ alignSelf: 'flex-end' }}>
              <Button type="primary" size="small" htmlType="submit" icon={<PlusOutlined />}>Add</Button>
            </div>
          </div>
        </Form>
      </Card>

      {/* Drivers table */}
      <Form form={editForm} component={false}>
        <Table
          dataSource={drivers}
          columns={columns}
          rowKey="_id"
          loading={loading}
          pagination={false}
          size="small"
          style={{ borderRadius: 8, overflow: 'hidden' }}
          locale={{ emptyText: 'No drivers yet — add one above' }}
        />
      </Form>
    </div>
  );
};

const labelStyle = { fontSize: 11, color: '#888', marginBottom: 2 };

export default OpcDrivers;
